import auth0 from 'auth0-js';

import history from './history';
import store from '../store';
import { setUser, setToken, showError, showLoader } from '../actions';
import service from '../utils/services';
import { setUser as raygunSetUser } from './raygun';

const databaseConnection = 'Username-Password-Authentication';

class Auth {
  userProfile;
  tokenRenewalTimeout;
  manage;

  auth0 = new auth0.WebAuth({
    domain: process.env.REACT_APP_AUTH0_DOMAIN,
    clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
    redirectUri: process.env.REACT_APP_REDIRECT_URL,
    audience: `https://${process.env.REACT_APP_AUTH0_AUDIENCE}/api/v2/`,
    scope:
      'openid profile email read:current_user update:current_user_metadata',
    responseType: 'token id_token'
  });

  constructor() {
    this.login = this.login.bind(this);
    this.signup = this.signup.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.changePassword = this.changePassword.bind(this);
    this.updateUserMetadata = this.updateUserMetadata.bind(this);

    if (this.isAuthenticated()) {
      const state = store.getState();
      const { user } = state;
      if (user) {
        raygunSetUser(user);
      }
    }

    this.scheduleRenewal();
  }

  login({ email: username, password }, loginError) {
    this.auth0.login(
      {
        username,
        password
      },
      error => {
        if (error) loginError(error);
      }
    );
  }

  loginSocial(connection) {
    this.auth0.authorize({
      connection
    });
  }

  signup({ email, password }, errorcb) {
    this.auth0.redirect.signupAndLogin(
      {
        connection: databaseConnection,
        email,
        password
      },
      (error, authResult) => {
        if (error) {
          errorcb(error);
        } else {
          this.setSession(authResult);
          store.dispatch(setToken(authResult.idToken));
        }
      }
    );
  }

  handleAuthentication() {
    this.auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        store.dispatch(setToken(authResult.idToken));
        service.getProfile().then(response => {
          const profile = response.data;
          if (profile) {
            this.signInComplete(profile, authResult);
          } else {
            this.signUpUser(authResult);
          }
        });
      } else {
        history.replace('/');
      }
    });
  }

  signUpUser = async authResult => {
    try {
      const { user } = store.getState();
      const {
        given_name,
        family_name,
        email: Email,
        email_verified: EmailVerified,
        sub: Auth0UserId
      } = authResult.idTokenPayload;

      const response = await service.signUpUser({
        FirstName: given_name || user.FirstName,
        LastName: family_name || user.LastName,
        Email,
        EmailVerified,
        Auth0UserId
      });

      this.signInComplete(response.data, authResult);
    } catch (error) {
      const message = error.request.response;
      if (message.toLowerCase() === '"user already exists"') {
        service
          .getUserByEmail(authResult.idTokenPayload.email)
          .then(resp => resp.data)
          .then(user => {
            history.push({
              pathname: '/login/exists',
              state: { userdetail: user }
            });
          });
      } else {
        store.dispatch(showError({ message, title: 'ERROR' }));
      }
    }
  };

  signInComplete(profile, authResult) {
    this.setSession(authResult);
    store.dispatch(setUser(profile));
    raygunSetUser(profile);

    if (profile.SignUpStep === 5) {
      if (profile.PeopleVineCustomerNo) {
        this.getPeopleVineCredentials().then(password => {
          this.gotoPeopleVine({
            role: profile.Role,
            email: profile.Email,
            password
          });
        });
      } else {
        service.postPeopleVineInfo().then(
          this.completePeopleVineRegistration({
            role: profile.Role,
            email: profile.Email
          })
        );
      }
    } else {
      history.replace(`/profile/setup/${profile.SignUpStep + 1}`);
    }
  }

  completePeopleVineRegistration = ({ role, email }) => {
    this.getPeopleVineCredentials().then(password => {
      this.gotoPeopleVine({ role, email, password });
    });
  };

  getPeopleVineCredentials = () => {
    return service.getPeopleVineRedirectInfo().then(res => res.data);
  };

  gotoPeopleVine = ({ role, email, password }) => {
    window.location.href =
      role === 'member'
        ? `${
            process.env.REACT_APP_PEOPLEVINE_BASE_URL
          }/login?auth=true&email=${email}&password=${password}&reroute=/locations`
        : `${
            process.env.REACT_APP_PEOPLEVINE_TRAINER_BASE_URL
          }/login?auth=true&email=${email}&password=${password}&reroute=/`;
  };

  setSession(authResult) {
    // Set the time that the access token will expire at
    let expiresAt = JSON.stringify(
      authResult.expiresIn * 1000 + new Date().getTime()
    );

    localStorage.setItem('access_token', authResult.accessToken);
    localStorage.setItem('id_token', authResult.idToken);
    localStorage.setItem('expires_at', expiresAt);

    // schedule a token renewal
    this.scheduleRenewal();
  }

  getAccessToken() {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }
    return accessToken;
  }

  getIdToken() {
    const idToken = localStorage.getItem('id_token');
    if (!idToken) {
      throw new Error('No id token found');
    }
    return idToken;
  }

  logout() {
    clearTimeout(this.tokenRenewalTimeout);
    store.dispatch(setToken(null));

    // Clear access token and ID token from local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('state');

    // navigate to the home route
    history.replace('/');

    raygunSetUser({ Email: '', FirstName: '', LastName: '' });
  }

  isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    let expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    return new Date().getTime() < expiresAt;
  }

  renewToken() {
    this.auth0.checkSession({}, (err, authResult) => {
      if (err) {
        history.replace('/login');
        this.logout();
      } else {
        store.dispatch(setToken(authResult.idToken));
        this.setSession(authResult);
      }
    });
  }

  scheduleRenewal() {
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    const delay = expiresAt - Date.now();
    if (delay > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewToken();
      }, delay);
    }
  }

  changePassword(email, success, error) {
    return this.auth0.changePassword(
      {
        connection: databaseConnection,
        email
      },
      (err, resp) => {
        if (err) {
          return error(err);
        }
        return success(resp);
      }
    );
  }

  updateUserMetadata(id, userMetadata, successcb, errorcb) {
    store.dispatch(showLoader());
    const token = this.getAccessToken();

    if (!this.manage) {
      this.manage = new auth0.Management({
        domain: process.env.REACT_APP_AUTH0_DOMAIN,
        token
      });
    }

    return this.manage.patchUserMetadata(id, userMetadata, (err, profile) => {
      if (err) {
        errorcb(err);
        return;
      }

      this.auth0.checkSession({}, (err, authResult) => {
        store.dispatch(setToken(authResult.idToken));
        successcb();
      });
    });
  }
}

const AuthService = new Auth();

export default AuthService;



// WEBPACK FOOTER //
// ./src/utils/AuthService.js